/**
 * Geometry utilities for canvas coordinate transforms.
 *
 * All functions are pure and stateless. Coordinate system is Y-down
 * to match browser/CSS conventions.
 */

import type { Camera, WorldPoint, ScreenPoint, WorldRect, ScreenRect, ViewportSize } from './types';

// =============================================================================
// Coordinate Transforms
// =============================================================================

/**
 * Convert world coordinates to screen coordinates.
 *
 * World → Screen transform:
 *   screenX = (worldX - camera.x) * zoom + viewportWidth/2
 *   screenY = (worldY - camera.y) * zoom + viewportHeight/2
 */
export function worldToScreen(
	world: WorldPoint,
	camera: Camera,
	viewport: ViewportSize
): ScreenPoint {
	return {
		x: (world.x - camera.x) * camera.zoom + viewport.width / 2,
		y: (world.y - camera.y) * camera.zoom + viewport.height / 2
	};
}

/**
 * Convert screen coordinates to world coordinates.
 *
 * Screen → World transform:
 *   worldX = (screenX - viewportWidth/2) / zoom + camera.x
 *   worldY = (screenY - viewportHeight/2) / zoom + camera.y
 */
export function screenToWorld(
	screen: ScreenPoint,
	camera: Camera,
	viewport: ViewportSize
): WorldPoint {
	return {
		x: (screen.x - viewport.width / 2) / camera.zoom + camera.x,
		y: (screen.y - viewport.height / 2) / camera.zoom + camera.y
	};
}

/**
 * Convert a world rect to screen rect.
 */
export function worldRectToScreen(
	rect: WorldRect,
	camera: Camera,
	viewport: ViewportSize
): ScreenRect {
	const topLeft = worldToScreen({ x: rect.x, y: rect.y }, camera, viewport);
	return {
		x: topLeft.x,
		y: topLeft.y,
		width: rect.width * camera.zoom,
		height: rect.height * camera.zoom
	};
}

/**
 * Convert a screen rect to world rect.
 */
export function screenRectToWorld(
	rect: ScreenRect,
	camera: Camera,
	viewport: ViewportSize
): WorldRect {
	const topLeft = screenToWorld({ x: rect.x, y: rect.y }, camera, viewport);
	return {
		x: topLeft.x,
		y: topLeft.y,
		width: rect.width / camera.zoom,
		height: rect.height / camera.zoom
	};
}

// =============================================================================
// Rectangle Operations
// =============================================================================

/**
 * Check if two rectangles intersect.
 */
export function rectsIntersect(a: WorldRect, b: WorldRect): boolean {
	return !(
		a.x + a.width <= b.x ||
		b.x + b.width <= a.x ||
		a.y + a.height <= b.y ||
		b.y + b.height <= a.y
	);
}

/**
 * Check if rect `a` fully contains rect `b`.
 */
export function rectContains(a: WorldRect, b: WorldRect): boolean {
	return (
		b.x >= a.x &&
		b.y >= a.y &&
		b.x + b.width <= a.x + a.width &&
		b.y + b.height <= a.y + a.height
	);
}

/**
 * Check if a rectangle contains a point.
 */
export function rectContainsPoint(rect: WorldRect, point: WorldPoint): boolean {
	return (
		point.x >= rect.x &&
		point.x <= rect.x + rect.width &&
		point.y >= rect.y &&
		point.y <= rect.y + rect.height
	);
}

/**
 * Get the bounding rectangle of a node given its top-left position and size.
 */
export function getNodeBounds(
	position: WorldPoint,
	size: { width: number; height: number }
): WorldRect {
	return {
		x: position.x,
		y: position.y,
		width: size.width,
		height: size.height
	};
}

// =============================================================================
// Frustum / Viewport
// =============================================================================

/**
 * Get the visible world rect (frustum) from camera and viewport.
 */
export function getVisibleWorldRect(camera: Camera, viewport: ViewportSize): WorldRect {
	const halfWidth = viewport.width / 2 / camera.zoom;
	const halfHeight = viewport.height / 2 / camera.zoom;

	return {
		x: camera.x - halfWidth,
		y: camera.y - halfHeight,
		width: halfWidth * 2,
		height: halfHeight * 2
	};
}

/**
 * Check if a node is visible in the camera frustum.
 *
 * @param position Node top-left position in world coordinates
 * @param size Node size in world units
 * @param camera Current camera state
 * @param viewport Viewport dimensions
 * @param margin Extra margin in world units (for culling hysteresis)
 */
export function isNodeInFrustum(
	position: WorldPoint,
	size: { width: number; height: number },
	camera: Camera,
	viewport: ViewportSize,
	margin: number = 0
): boolean {
	const frustum = getVisibleWorldRect(camera, viewport);
	const nodeBounds = getNodeBounds(position, size);

	// Expand frustum by margin
	const expandedFrustum: WorldRect = {
		x: frustum.x - margin,
		y: frustum.y - margin,
		width: frustum.width + margin * 2,
		height: frustum.height + margin * 2
	};

	return rectsIntersect(expandedFrustum, nodeBounds);
}

// =============================================================================
// Camera Operations
// =============================================================================

/**
 * Clamp zoom to min/max bounds.
 */
export function clampZoom(zoom: number, minZoom: number, maxZoom: number): number {
	return Math.max(minZoom, Math.min(maxZoom, zoom));
}

/**
 * Calculate new camera position after zoom, keeping a world point stationary.
 *
 * This is used for "zoom toward cursor" - the point under the cursor
 * should stay at the same screen position after zooming.
 */
export function zoomTowardPoint(
	camera: Camera,
	newZoom: number,
	screenPoint: ScreenPoint,
	viewport: ViewportSize
): Camera {
	// Get the world point under the screen point before zoom
	const worldPoint = screenToWorld(screenPoint, camera, viewport);

	// After zoom, worldPoint should still map to screenPoint
	// screenX = (worldX - camera.x) * newZoom + viewport.width/2
	// Solve for camera.x:
	// camera.x = worldX - (screenX - viewport.width/2) / newZoom

	return {
		x: worldPoint.x - (screenPoint.x - viewport.width / 2) / newZoom,
		y: worldPoint.y - (screenPoint.y - viewport.height / 2) / newZoom,
		zoom: newZoom
	};
}

/**
 * Pan camera by screen delta.
 *
 * Convert screen pixels to world units and subtract from camera position.
 */
export function panByScreenDelta(camera: Camera, deltaX: number, deltaY: number): Camera {
	return {
		x: camera.x - deltaX / camera.zoom,
		y: camera.y - deltaY / camera.zoom,
		zoom: camera.zoom
	};
}

/**
 * Calculate camera to fit all given nodes with padding.
 */
export function fitToNodes(
	nodes: Array<{ position: WorldPoint; size: { width: number; height: number } }>,
	viewport: ViewportSize,
	padding: number = 50,
	minZoom: number = 0.1,
	maxZoom: number = 10,
	maxFill: number = 1.0,
): Camera {
	if (nodes.length === 0) {
		return { x: 0, y: 0, zoom: 1 };
	}

	// Find bounding box of all nodes
	let minX = Infinity;
	let minY = Infinity;
	let maxX = -Infinity;
	let maxY = -Infinity;

	for (const node of nodes) {
		const bounds = getNodeBounds(node.position, node.size);
		minX = Math.min(minX, bounds.x);
		minY = Math.min(minY, bounds.y);
		maxX = Math.max(maxX, bounds.x + bounds.width);
		maxY = Math.max(maxY, bounds.y + bounds.height);
	}

	const contentWidth = maxX - minX;
	const contentHeight = maxY - minY;
	const centerX = (minX + maxX) / 2;
	const centerY = (minY + maxY) / 2;

	// Calculate zoom to fit content with padding
	const availableWidth = viewport.width - padding * 2;
	const availableHeight = viewport.height - padding * 2;

	let zoom = 1;
	if (contentWidth > 0 && contentHeight > 0) {
		zoom = Math.min(availableWidth / contentWidth, availableHeight / contentHeight);
	}

	// maxFill caps how much of the viewport the content should occupy (0-1).
	// E.g. 0.5 means content takes up at most half the viewport.
	const fill = Math.max(0.01, Math.min(1, maxFill));
	zoom *= fill;

	zoom = clampZoom(zoom, minZoom, maxZoom);

	return { x: centerX, y: centerY, zoom };
}
